import { lazy, Suspense, useEffect, useState } from "react";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { Select } from "@/components/Select";
import {
  useGetActiveLanguages,
  useGetLanguages,
  usePostUpdatePolicy,
} from "@/requests/hooks/comply";
import {
  faExternalLink,
  faInfoCircle,
  faWarning,
} from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Controller, ControllerRenderProps } from "react-hook-form";
import { toast } from "react-toastify";
import ImageUpload from "@/components/ImageUpload";
import { readFileAsBase64 } from "@/utils/readFileAsBase64";
import { useQueryClient } from "@tanstack/react-query";
import { IDropdownOption } from "@/components/Select/Select";
import { getAppConfig } from "@/utils/appManager";
import { DeletePolicyModal } from "@/components/Modal";
import { Tooltip } from "@/components/Tooltip";
import Checkbox from "@/components/Checkbox";
import { getPolicyIcons } from "@/utils/getPolicyIcons";
import { bool, InferType, mixed, object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import PolicyEditorFormLoader from "@/routes/Comply/Policies/Manage/PolicyEditorFormLoader";
import { Alert } from "@/components/Alert";

const PolicyTextEditor = lazy(() => import("@/components/PolicyTextEditor"));

interface IProps {
  updateType: "EDIT" | "ADD";
  policyType: "PDF" | "TEXT";
  existingPolicy?: IPolicy;
}

const PolicyEditor = ({ updateType, policyType, existingPolicy }: IProps) => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { data: languages } = useGetLanguages();
  const { data: activeLanguages } = useGetActiveLanguages();
  const {
    mutate: updatePolicy,
    isPending: isUpdatePolicyPending,
    isSuccess: isUpdatePolicySuccess,
  } = usePostUpdatePolicy();
  const { id } = useParams();
  const { complyPDFPolicyUrl } = getAppConfig();

  const getIconMapping = (icon?: string) => {
    const iconOptions = getPolicyIcons();
    return (
      iconOptions?.find(iconOption => iconOption.value === icon) ||
      iconOptions[0]
    );
  };

  const formSchema = object({
    icon: object({
      label: object().required(t("comply.policies.common.icon.required")),
      value: string().required(t("comply.policies.common.icon.required")),
    }),
    name: string().required(t("comply.policies.common.name.required")),
    language: object({
      label: string().required(t("comply.policies.common.language.required")),
      value: string().required(t("comply.policies.common.language.required")),
    }),
    content: string().when({
      is: () => policyType === "TEXT",
      then: () =>
        string().required(t("comply.policies.common.content.required")),
      otherwise: () => string().nullable(),
    }),

    pdfUrl: mixed().when({
      is: () => policyType === "PDF",
      then: () => mixed().required(t("comply.policies.common.pdfUrl.required")),
      otherwise: () => mixed().nullable(),
    }),
    active: bool(),
  });

  type IPolicyForm = InferType<typeof formSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    trigger,
    resetField,
    control,
  } = useForm<IPolicyForm>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      content: existingPolicy?.content,
      pdfUrl: existingPolicy?.pdfUrl,
      icon: getIconMapping(existingPolicy?.icon),
      language: {
        value:
          (existingPolicy?.language || searchParams?.get("language")) ?? "",
        label:
          (existingPolicy?.language || searchParams?.get("language")) ?? "",
      },
      name: existingPolicy?.name,
      active: existingPolicy?.active,
    },
  });

  const getActiveLanguages = (): IDropdownOption[] | undefined => {
    const findActiveLanguage = (language: ILanguage) =>
      activeLanguages?.find(languageId => languageId === language?.id);

    return languages?.filter(findActiveLanguage).map(language => ({
      label: language.code,
      value: language.code,
    }));
  };

  useEffect(() => {
    if (!isUpdatePolicySuccess) return;

    const successMessage =
      updateType === "ADD"
        ? t("comply.policies.add.success")
        : t("comply.policies.edit.success");

    toast.success(successMessage);
    queryClient.removeQueries({ queryKey: ["comply", "list-policies"] });
    goBackToPolicies();
  }, [isUpdatePolicySuccess]);

  const goBackToPolicies = () => {
    navigate(
      "/comply/policies" +
        (!!state
          ? state
          : getValues("language.value")
            ? "?language=" + getValues("language.value")
            : ""),
    );
  };

  const submitPolicy = async (policy: IPolicyForm) => {
    await trigger();
    if (!isValid) return;

    const pdfFile: File = (policy?.pdfUrl as FileList)?.[0];
    let pdfUrl: string | undefined;

    if (!!pdfFile?.name && !pdfFile?.name?.includes(complyPDFPolicyUrl))
      pdfUrl = (await readFileAsBase64(pdfFile)) as string;

    if (existingPolicy) {
      updatePolicy({
        ...existingPolicy,
        ...policy,
        icon: policy?.icon?.value || existingPolicy?.icon,
        language: policy.language.value!,
        pdfUrl,
        pdfName: pdfFile?.name,
      });
    } else {
      updatePolicy({
        ...policy,
        icon: policy.icon.value!,
        version: 1,
        language: policy.language.value!,
        active: true,
        pdfUrl,
        pdfName: pdfFile?.name,
      });
    }
  };

  const handleClearLogo = () => {
    resetField("pdfUrl");
  };

  const toggleDeleteModal = () => setIsDeleteModalOpen(isOpen => !isOpen);

  const renderLanguageSelect = ({
    field,
  }: {
    field: ControllerRenderProps<IPolicyForm, "language">;
  }) => (
    <div className="w-full md:max-w-[160px]">
      <Select
        {...register("language")}
        label={t("comply.policies.common.language.label")}
        options={getActiveLanguages()}
        closeMenuOnSelect
        variant={errors?.language?.label?.message ? "error" : undefined}
        helperText={errors?.language?.label?.message}
        {...field}
      />
    </div>
  );

  const renderLanguageSection = () => {
    if (updateType === "EDIT") {
      return (
        <div className="w-full md:max-w-[160px]">
          <Input
            name="language"
            label={t("comply.policies.common.language.label")}
            disabled={true}
            value={getValues("language.value")}
          />
        </div>
      );
    }

    return (
      <Controller
        control={control}
        name="language"
        render={renderLanguageSelect}
      />
    );
  };

  const renderIconSelect = ({
    field,
  }: {
    field: ControllerRenderProps<IPolicyForm, "icon">;
  }) => (
    <div className="w-full md:max-w-[160px]">
      <Select
        options={getPolicyIcons()}
        label={t("comply.policies.common.icon.label")}
        {...register("icon")}
        closeMenuOnSelect
        variant={errors?.icon?.label?.message ? "error" : undefined}
        helperText={errors?.icon?.label?.message}
        {...field}
      />
    </div>
  );

  const renderPolicyEditorLoader = () => (
    <PolicyEditorFormLoader updateType={updateType} />
  );

  const renderEditorInfoAlertTitle = () => (
    <>
      Each policy section heading should be formatted with an H2 element.
      Sections will be divided by H2 headings for better readability.
    </>
  );

  const renderEditor = ({
    field,
  }: {
    field: ControllerRenderProps<IPolicyForm, "content">;
  }) => (
    <Suspense fallback={renderPolicyEditorLoader()}>
      <Alert
        variant="info"
        icon={faInfoCircle}
        titleSlot={renderEditorInfoAlertTitle()}
      />
      <PolicyTextEditor
        helperText={errors?.content?.message}
        {...register("content")}
        {...field}
        content={field.value}
      />
    </Suspense>
  );

  const renderPdfUpload = () => (
    <div className="flex w-full flex-col items-center gap-20 bg-white md:flex-row">
      <ImageUpload
        type="policy"
        helperText={errors?.pdfUrl?.message}
        testId="policy-img"
        onClear={handleClearLogo}
        {...register("pdfUrl")}
      />
    </div>
  );

  const renderContentEditorOrLoader = () => {
    if (policyType === "PDF") {
      return (
        <Controller control={control} name="pdfUrl" render={renderPdfUpload} />
      );
    }
    return (
      <Controller control={control} name="content" render={renderEditor} />
    );
  };

  const renderExternalLinkIcon = () => {
    if (policyType === "PDF" && updateType === "EDIT")
      return (
        <a href={existingPolicy?.pdfUrl} target="_blank">
          <FontAwesomeIcon icon={faExternalLink} />
        </a>
      );
  };

  const renderPolicyName = () => (
    <Input
      placeholder={t("comply.policies.common.name.placeholder")}
      label={t("comply.policies.common.name.title")}
      {...register("name")}
      variant={errors?.name?.message ? "error" : undefined}
      helperText={errors?.name?.message}
      trailingSlot={renderExternalLinkIcon()}
    />
  );

  const renderCTAs = () => (
    <div className="mt-30 flex flex-col items-center justify-between gap-20 md:flex-row">
      {updateType === "EDIT" ? (
        <Button
          as="button"
          variant="link"
          type="button"
          onClick={toggleDeleteModal}>
          {t("comply.policies.delete.submit")}
        </Button>
      ) : (
        <div />
      )}
      <Button
        as="button"
        variant="primary"
        type="button"
        onClick={handleSubmit(submitPolicy)}
        loading={isUpdatePolicyPending}
        width="md">
        {t(`comply.policies.${updateType === "ADD" ? "add" : "edit"}.submit`)}
      </Button>
    </div>
  );

  const renderCommonPolicyContentEditors = () => (
    <div className="flex flex-col gap-20 md:flex-row md:gap-10">
      <Controller control={control} name="icon" render={renderIconSelect} />
      {renderPolicyName()}
      {renderLanguageSection()}
    </div>
  );

  const renderModal = () => (
    <DeletePolicyModal
      isOpen={isDeleteModalOpen}
      closeModal={toggleDeleteModal}
      id={id}
      successRedirect={goBackToPolicies}
    />
  );

  const renderCheckbox = ({
    field,
  }: {
    field: ControllerRenderProps<IPolicyForm, "active">;
  }) => (
    <Checkbox
      label={t("comply.policies.common.active.checkboxLabel")}
      checked={!!getValues("active")}
      {...register("active")}
      {...field}
    />
  );

  const renderEnableToggle = () =>
    updateType === "EDIT" && (
      <div className="mb-20 flex flex-row items-center justify-between rounded-sm border-y border-gray bg-gray-light p-20">
        <div className="flex items-center gap-20">
          <FontAwesomeIcon
            icon={faWarning}
            className="h-20 w-20 text-primary"
          />
          {t("comply.policies.common.active.warning")}
        </div>

        <Tooltip content={t("comply.policies.common.active.tooltip")}>
          <Controller control={control} name="active" render={renderCheckbox} />
        </Tooltip>
      </div>
    );

  return (
    <>
      <form
        className="flex flex-col gap-20 md:gap-10"
        name="policy-editor-form"
        id="policy-editor-form">
        {renderEnableToggle()}
        <div className="flex flex-col gap-20 rounded-sm bg-gray-light p-20">
          {renderCommonPolicyContentEditors()}
          {renderContentEditorOrLoader()}
          {renderCTAs()}
        </div>
      </form>
      {renderModal()}
    </>
  );
};

export default PolicyEditor;
